/**
 * Query Error Boundary Component
 * 
 * Integrates React Query's error handling with our error boundary system.
 * Provides automatic retry functionality for failed queries.
 */

import React, { ReactNode, useCallback } from 'react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from 'src/components/ui/button/button';
import { Alert, AlertDescription, AlertTitle } from 'src/components/ui/alert';
import { Card, CardContent } from 'src/components/ui/card';
import { errorHandlingService } from 'src/services/error-handling';
import { Logger } from 'src/utils/logger';

const logger = Logger.createScopedLogger('QueryErrorBoundary');

interface QueryErrorBoundaryProps {
  children: ReactNode;
  fallbackRender?: (props: FallbackProps & { processed: ReturnType<typeof errorHandlingService.processError> }) => ReactNode;
  onError?: (error: Error) => void;
  variant?: 'card' | 'inline' | 'full-page';
}

/**
 * Query Error Boundary with automatic retry support
 */
const QueryErrorBoundary: React.FC<QueryErrorBoundaryProps> = ({
  children,
  fallbackRender,
  onError,
  variant = 'card',
}) => {
  const handleError = useCallback((error: Error) => {
    logger.error('Query error caught by boundary', { error: error.message });
    
    errorHandlingService.handleError(error, {
      context: 'QueryErrorBoundary',
      showToast: false, // Fallback UI will show error
    });

    onError?.(error);
  }, [onError]);

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          onError={handleError}
          fallbackRender={(props) => {
            const processed = errorHandlingService.processError(props.error);
            
            if (fallbackRender) {
              return fallbackRender({ ...props, processed });
            }

            switch (variant) {
              case 'inline':
                return <InlineErrorFallback {...props} processed={processed} />;
              case 'full-page':
                return <FullPageErrorFallback {...props} processed={processed} />;
              case 'card':
              default:
                return <CardErrorFallback {...props} processed={processed} />;
            }
          }}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

/**
 * Common props for fallback components
 */
interface ErrorFallbackBaseProps extends FallbackProps {
  processed: ReturnType<typeof errorHandlingService.processError>;
}

/**
 * Card-based error fallback (default)
 */
const CardErrorFallback: React.FC<ErrorFallbackBaseProps> = ({
  resetErrorBoundary,
  processed,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="tw-w-full">
      <CardContent className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-p-6 tw-text-center">
        <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100">
          <AlertCircle className="tw-h-6 tw-w-6 tw-text-red-600" />
        </div>
        
        <div>
          <h3 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-gray-900">
            {processed.title}
          </h3>
          <p className="tw-text-sm tw-text-gray-600">
            {processed.message}
          </p>
        </div>

        {processed.isRetryable && (
          <Button
            onClick={resetErrorBoundary}
            variant="outline"
            className="tw-flex tw-items-center tw-gap-2"
          >
            <RefreshCw className="tw-h-4 tw-w-4" />
            {t('actions:try_again', { defaultValue: 'Try Again' })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Inline error fallback (minimal)
 */
const InlineErrorFallback: React.FC<ErrorFallbackBaseProps> = ({
  resetErrorBoundary,
  processed,
}) => {
  const { t } = useTranslation();

  return (
    <Alert variant="destructive" className="tw-flex tw-items-center tw-justify-between">
      <div className="tw-flex tw-items-center tw-gap-2">
        <AlertCircle className="tw-h-4 tw-w-4" />
        <AlertDescription>{processed.message}</AlertDescription>
      </div>
      
      {processed.isRetryable && (
        <Button
          onClick={resetErrorBoundary}
          variant="ghost"
          size="sm"
          className="tw-flex tw-items-center tw-gap-1"
        >
          <RefreshCw className="tw-h-3 tw-w-3" />
          {t('actions:retry', { defaultValue: 'Retry' })}
        </Button>
      )}
    </Alert>
  );
};

/**
 * Full page error fallback
 */
const FullPageErrorFallback: React.FC<ErrorFallbackBaseProps> = ({
  resetErrorBoundary,
  processed,
}) => {
  const { t } = useTranslation();

  return (
    <div className="tw-flex tw-min-h-[400px] tw-flex-col tw-items-center tw-justify-center tw-p-8">
      <div className="tw-mx-auto tw-max-w-md tw-text-center">
        <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100">
          <AlertCircle className="tw-h-8 tw-w-8 tw-text-red-600" />
        </div>

        <h2 className="tw-mb-2 tw-text-xl tw-font-semibold tw-text-gray-900">
          {processed.title}
        </h2>
        <p className="tw-mb-6 tw-text-gray-600">
          {processed.message}
        </p>

        <div className="tw-flex tw-justify-center tw-gap-3">
          {processed.isRetryable && (
            <Button
              onClick={resetErrorBoundary}
              className="tw-flex tw-items-center tw-gap-2"
            >
              <RefreshCw className="tw-h-4 tw-w-4" />
              {t('actions:try_again', { defaultValue: 'Try Again' })}
            </Button>
          )}
          
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
          >
            {t('actions:refresh_page', { defaultValue: 'Refresh Page' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export { 
  QueryErrorBoundary, 
  CardErrorFallback, 
  InlineErrorFallback, 
  FullPageErrorFallback 
};
export default QueryErrorBoundary;
